import { RefObject, useEffect, useRef, useState } from 'react';

const ResizeObserver = (window as any).ResizeObserver;

const BOTTOM_THRESHOLD_PX = 80;

const isNearBottom = (el: HTMLDivElement): boolean => {
  const { scrollTop, scrollHeight, clientHeight } = el;
  return scrollHeight - scrollTop - clientHeight < BOTTOM_THRESHOLD_PX;
};

/** Keeps scroll position in sync with "follow latest" behavior and exposes the jump-to-latest control. */
export function useChatFollowLatestScroll({
  scrollRootRef,
  contentRef,
}: {
  scrollRootRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
}) {
  // Doesnt trigger re-renders
  const followsLatestRef = useRef(true);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  // When the user manually scrolls, update whether we're following the latest message
  useEffect(() => {
    const el = scrollRootRef.current;
    if (!el) return;
    const onScroll = () => {
      const atBottom = isNearBottom(el);
      followsLatestRef.current = atBottom;
      setShowJumpToLatest(!atBottom);
    };
    // passive: true — tells the browser we won't call preventDefault, enabling scroll performance optimizations
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollRootRef]);

  // Auto-scroll when content grows: handles both new messages and in-progress streaming tokens
  // (messageCount alone wouldn't catch streaming, since the count stays the same while tokens arrive)
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const node = contentRef.current;
    if (!node) return;
    const ro = new ResizeObserver(() => {
      const el = scrollRootRef.current;
      // Only scroll if the user hasn't manually scrolled up
      if (followsLatestRef.current && el) el.scrollTop = el.scrollHeight;
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [contentRef, scrollRootRef]);

  const scrollToBottom = () => {
    const el = scrollRootRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  return {
    showJumpToLatest,
    // Re-enable follow mode and snap to the latest message
    onJumpToLatestClick: () => {
      followsLatestRef.current = true;
      scrollToBottom();
    },
    // Called externally (e.g. when the user sends a message) to re-enable follow mode
    setFollowingLatest: (following: boolean) => {
      followsLatestRef.current = following;
    },
  };
}
